<template>
  <div :class="[
    'card',
    {
      'soft-shadow': shadow,
      'border-0': !border
    }
  ]">
    <div :class="[
      'card-body',
      'position-relative',
      {
        'p-0': !padding
      }
    ]">
      <div class="media align-items-top">
        <div class="media-left">
          <a
            href="#"
            class="photo-management"
            @click.prevent="$emit('showImages', person.id)"
            v-if="canManage"
          >
            <span class="profile-img md mr-3" :style="`background-image: url(${person.image});`"></span>
            <span class="icon">
              <i class="fa fa-camera fa-fw"></i>
            </span>
          </a>
          <span
            v-if="!canManage"
            class="profile-img md mr-3"
            :style="`background-image: url(${person.image});`"
          ></span>
        </div>
        <div class="media-body">
          <div class="row">
            <div
              :class="[
                {
                  'col-12': size === 'sm',
                  'col-sm-6': size === 'lg',
                },
                'mb-3 mb-sm-0',
              ]"
            >
              <div class="font-weight-bold d-block d-md-flex">
                <span
                  v-if="hasNickname"
                  class="d-block text-nowrap"
                >
                  {{ person.display_name }} ({{ person.first_name }} {{ person.last_name }})
                </span>
                <span
                  v-else
                  class="d-block text-nowrap"
                >
                  {{ person.first_name }} {{ person.last_name }}
                </span>
              </div>
              <div
                v-if="person.type !== 'staff' || showStaffPhone"
              >
                {{ person.preferred_phone }}
              </div>
              <div
                v-else
                class="text-muted"
              >
                {{ person.office_phone }}
              </div>
              <div>
                <a class="text-flat-peter-river" :href="`mailto:${person.preferred_email}`">
                  {{ person.preferred_email }}
                </a>
              </div>
              <div
                v-if="person.preferred_email !== person.email"
              >
                <a class="text-flat-peter-river" :href="`mailto:${person.email}`">
                  {{ person.email }}
                </a>
              </div>
            </div>
            <div
              v-if="(canManage || canViewBirthdayAndAnniversary) && (!hideBirthday || !hideAnniversary)"
              class="col-12 col-sm-6"
            >
              <div
                v-if="birthday && !hideBirthday"
                class="mb-2"
              >
                <div class="extra-small text-muted text-uppercase">Birthday</div>
                <div>{{ birthday }}</div>
              </div>
              <div
                v-if="hireAnniversary && !hideAnniversary"
              >
                <div class="extra-small text-muted text-uppercase">Joined Company</div>
                <div>{{ hireAnniversary }}</div>
                <div class="small text-muted">This is NOT related to commission cap.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <hr>
        <div class="d-flex align-items-center">
          <div class="small text-muted text-nowrap">
            <i class="fa fa-building mr-1"></i>
            {{ person.office_name }}
          </div>
          <span class="small text-muted ml-3 mr-auto text-nowrap">
            <i class="fa fa-user mr-1"></i>
            {{ person.email }}
          </span>
          <div
            v-if="canManage && onboardingProgressUrl === null"
            class="mx-3"
          >
            <i
              class="fa fa-rocket"
            ></i>
            <i
              v-b-tooltip.hover
              :title="`Onboarding project does not exist for ${person.name}`"
              class="fa fa-exclamation-triangle text-danger mr-1"
            ></i>
          </div>
          <div
            v-if="canManage && onboardingProgressUrl !== null"
            class="ml-1 mr-2"
          >
            <div
              class="media clickable"
              @click.prevent="openOnboardingProject()"
            >
              <div class="media-left">
                <i
                  class="fa fa-rocket mr-2"
                ></i>
              </div>
              <div
                class="media-body"
                style="width:60px;"
              >
                <b-progress
                  :max="100"
                  height="1.5rem"
                  :title="`${person.name} is ${onboardingProgress}% onboarded`"
                  v-b-tooltip.hover
                >
                  <b-progress-bar
                    :value="onboardingProgress"
                    :variant="onboardingProgressVariant"
                  ></b-progress-bar>
                </b-progress>
              </div>
            </div>
          </div>
          <div
            v-if="canManage"
            class="ml-1"
          >
            <person-action-dropdown
              :user-id="person.rosters_id"
            ></person-action-dropdown>
          </div>
          <div
            v-if="showProfileLink"
            class="ml-1"
          >
            <a
              :href="urls.people.show(personId)"
              class="btn btn-sm btn-white"
            >
              <slot name="view-button">
                View
                <i class="fa fa-arrow-right ml-1"></i>
              </slot>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.photo-management {
  position: relative;
  .icon {
    position: absolute;
    width: 50px;
    height: 50px;
    top: 50%;
    margin-top: -25px;
    left: 50%;
    margin-left: -18px;
    color: #111;
    display: none;
  }
  &:hover {
    .profile-img {
      opacity: 0.3;
    }
    .icon {
      display: block;
    }
  }
}

.clickable {
  cursor: pointer;
}
</style>

<script>
import urls from '@/api/urls'
import PersonActionDropdown from '@/components/PersonActionDropdown'
import { formatDate } from '@/lib/moment'
import pluralize from 'pluralize'

export default {
  components: {
    PersonActionDropdown
  },
  props: {
    person: {
      type: Object,
      required: true
    },
    personId: {
      type: [String, Number],
      default: null
    },
    canManage: {
      type: Boolean,
      default: false
    },
    canViewBirthdayAndAnniversary: {
      type: Boolean,
      default: false
    },
    showStaffPhone: {
      type: Boolean,
      default: false
    },
    showProfileLink: {
      type: Boolean,
      default: true
    },
    shadow: {
      type: Boolean,
      default: true
    },
    border: {
      type: Boolean,
      default: true
    },
    padding: {
      type: Boolean,
      default: true
    },
    hideAnniversary: {
      type: Boolean,
      default: false
    },
    hideBirthday: {
      type: Boolean,
      default: false
    },
    onboardingProjectUrl: {
      type: Number,
      default: null
    },
    onboardingProgress: {
      type: Number,
      default: null
    },
    size: {
      type: String,
      default: 'lg',
    }
  },
  data () {
    return {
      urls
    }
  },
  computed: {
    hasNickname () {
      return `${this.person.first_name} ${this.person.last_name}`.trim() !== (this.person.display_name || '').trim()
    },
    birthday () {
      let birthday = null
      if (this.person.birthday) {
        birthday = formatDate(this.person.birthday, 'MMMM Do')
      }
      return birthday
    },
    thisHireAtAnniversary () {
      let thisHireAtAnniversary = this.person.hired_at
      if (thisHireAtAnniversary) {
        thisHireAtAnniversary = moment(thisHireAtAnniversary).set('year', moment().get('year'))
      }
      return thisHireAtAnniversary
    },
    hireAnniversary () {
      let hireAnniversary = null
      if (this.thisHireAtAnniversary) {
        let years = this.thisHireAtAnniversary.diff(this.person.hired_at, 'years')
        let label = ''
        if (moment().isBefore(this.thisHireAtAnniversary)) {
          label = 'will be '
        }
        hireAnniversary = `${formatDate(this.person.hired_at, 'MMMM Do')} (${label}${years} ${pluralize('year', years)})`
      }
      return hireAnniversary
    },
    cap () {
      return ''
    },
    canAnniversary () {
      return ''
    },
    split () {
      return ''
    },
    onboardingProgressVariant () {
      if (this.onboardingProgress < 80) {
        return 'danger'
      } else if (this.onboardingProgress < 100) {
        return 'warning'
      } else {
        return 'success'
      }
    }
  },
  methods: {
    openOnboardingProject() {
      if (this.onboardingProjectUrl) {
        window.open(this.onboardingProjectUrl)
      } else {
        alert("This person's onboarding project hasn't been setup yet.");
      }
    }
  }
}
</script>